import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { supabase } from '../supabase/supabase';

const { 
  FiFileText, FiCheckCircle, FiXCircle, FiTrendingUp, 
  FiSearch, FiFilter, FiChevronRight, FiClock,
  FiAward, FiActivity, FiMoreVertical, FiMessageSquare
} = FiIcons;

export default function Assessments() {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('recent'); // 'recent', 'by-quiz', 'grading'
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchSubmissions();
  }, []);

  async function fetchSubmissions() {
    try {
      const { data, error } = await supabase
        .from('assessment_submissions_2024')
        .select(`
          *,
          clients_2024 (name, email),
          quizzes_2024 (title, type, passing_score)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setSubmissions(data || []);
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  }

  const filteredSubmissions = submissions.filter(s => 
    s.clients_2024?.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.quizzes_2024?.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="max-w-7xl mx-auto space-y-8 pb-20">
      {/* Header */}
      <header className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
        <div>
          <h1 className="text-5xl font-black text-white tracking-tighter uppercase text-gradient">
            Assessment Center
          </h1>
          <p className="text-slate-400 mt-2 text-lg font-medium">
            Verify student <span className="text-indigo-400 font-bold">mastery</span> and provide feedback
          </p>
        </div>
        <div className="flex gap-3">
          <button className="bg-slate-900 border border-white/5 text-slate-400 px-6 py-3 rounded-2xl font-bold hover:text-white transition-all flex items-center gap-2">
            <SafeIcon icon={FiActivity} /> Global Stats
          </button>
          <button className="premium-gradient text-white px-6 py-3 rounded-2xl font-black shadow-lg shadow-indigo-600/20 active:scale-95 transition-all">
            Create Assessment
          </button>
        </div>
      </header>

      {/* Stats Bar */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {[
          { label: 'Average Score', value: '84%', icon: FiTrendingUp, color: 'indigo' },
          { label: 'Pass Rate', value: '92%', icon: FiAward, color: 'emerald' },
          { label: 'Pending Feedback', value: '14', icon: FiMessageSquare, color: 'amber' }
        ].map((stat, i) => (
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: i * 0.1 }}
            key={i}
            className="bg-slate-900/40 border border-white/5 p-6 rounded-[32px] flex items-center gap-6"
          >
            <div className={`w-14 h-14 bg-${stat.color}-500/10 text-${stat.color}-400 rounded-2xl flex items-center justify-center`}>
              <SafeIcon icon={stat.icon} className="text-2xl" />
            </div>
            <div>
              <p className="text-slate-500 text-[10px] font-black uppercase tracking-widest">{stat.label}</p>
              <h3 className="text-3xl font-black text-white mt-1">{stat.value}</h3>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Control Bar */}
      <div className="flex flex-col md:flex-row justify-between items-center bg-slate-900/20 p-2 rounded-[2.5rem] border border-white/5 backdrop-blur-xl">
        <div className="flex gap-1 p-1">
          {['recent', 'by-quiz', 'grading'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-8 py-3 rounded-[2rem] text-sm font-black uppercase tracking-tight transition-all ${
                activeTab === tab 
                  ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-600/20' 
                  : 'text-slate-500 hover:text-slate-300'
              }`}
            >
              {tab.replace('-', ' ')}
            </button>
          ))}
        </div>
        <div className="relative w-full md:w-80 px-2">
          <SafeIcon icon={FiSearch} className="absolute left-6 top-1/2 -translate-y-1/2 text-slate-500" />
          <input 
            type="text"
            placeholder="Filter results..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full bg-slate-950/50 border border-white/5 rounded-3xl py-3 pl-12 pr-4 text-white placeholder-slate-600 focus:outline-none focus:border-indigo-500/50 transition-all font-medium"
          />
        </div>
      </div>

      {/* Submissions List */}
      <div className="bg-slate-900/40 border border-white/5 rounded-[40px] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b border-white/5">
                <th className="px-8 py-6 text-[10px] font-black text-slate-500 uppercase tracking-widest">Student</th>
                <th className="px-8 py-6 text-[10px] font-black text-slate-500 uppercase tracking-widest">Assessment</th>
                <th className="px-8 py-6 text-[10px] font-black text-slate-500 uppercase tracking-widest">Score</th>
                <th className="px-8 py-6 text-[10px] font-black text-slate-500 uppercase tracking-widest">Status</th>
                <th className="px-8 py-6 text-[10px] font-black text-slate-500 uppercase tracking-widest text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredSubmissions.map((sub, i) => (
                <motion.tr 
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                  key={sub.id} 
                  className="group hover:bg-white/5 transition-colors cursor-pointer"
                >
                  <td className="px-8 py-6">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-indigo-600/20 rounded-xl flex items-center justify-center text-indigo-400 font-black">
                        {sub.clients_2024?.name.charAt(0)}
                      </div>
                      <div>
                        <p className="text-white font-bold">{sub.clients_2024?.name}</p>
                        <p className="text-[10px] text-slate-500 font-bold">{sub.clients_2024?.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-8 py-6">
                    <p className="text-white font-bold">{sub.quizzes_2024?.title}</p>
                    <p className="text-[10px] text-slate-500 font-black uppercase tracking-tighter">{sub.quizzes_2024?.type}</p>
                  </td>
                  <td className="px-8 py-6">
                    <span className={`text-xl font-black ${sub.score >= (sub.quizzes_2024?.passing_score || 70) ? 'text-emerald-400' : 'text-rose-400'}`}>
                      {sub.score}%
                    </span>
                  </td>
                  <td className="px-8 py-6">
                    {sub.is_passed ? (
                      <span className="bg-emerald-500/10 text-emerald-400 text-[10px] font-black px-3 py-1 rounded-full border border-emerald-500/20 uppercase tracking-widest flex items-center gap-2 w-fit">
                        <SafeIcon icon={FiCheckCircle} /> Passed
                      </span>
                    ) : (
                      <span className="bg-rose-500/10 text-rose-400 text-[10px] font-black px-3 py-1 rounded-full border border-rose-500/20 uppercase tracking-widest flex items-center gap-2 w-fit">
                        <SafeIcon icon={FiXCircle} /> Failed
                      </span>
                    )}
                  </td>
                  <td className="px-8 py-6 text-right">
                    <button className="p-2 text-slate-500 hover:text-white transition-colors">
                      <SafeIcon icon={FiMoreVertical} />
                    </button>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {filteredSubmissions.length === 0 && (
          <div className="p-20 text-center">
            <div className="w-20 h-20 bg-slate-800/50 rounded-full flex items-center justify-center text-slate-600 mx-auto mb-4">
              <SafeIcon icon={FiFileText} className="text-3xl" />
            </div>
            <h3 className="text-white font-black text-xl">No assessments found</h3>
            <p className="text-slate-500 mt-2 max-w-xs mx-auto">
              {searchQuery ? "Try adjusting your search filters" : "Start by creating your first assessment in the Course Editor"}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}